import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { translations } from '../../locales/pl';

interface CreateHouseholdDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
}

const CreateHouseholdDrawer: React.FC<CreateHouseholdDrawerProps> = ({
  open,
  onClose,
  onCreate,
}) => {
  const [name, setName] = useState<string>('');
  const [creating, setCreating] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setName('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      return;
    }

    try {
      setCreating(true);
      await onCreate(name.trim());
      handleClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setName('');
    onClose();
  };

  return (
    <Drawer anchor="bottom" open={open} onClose={handleClose}>
      <Box sx={{ padding: 3, minHeight: '300px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 2,
          }}
        >
          <Typography variant="h6">
            {translations.households.createHouseholdTitle}
          </Typography>
          <IconButton onClick={handleClose} aria-label={translations.common.close}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label={translations.households.householdNameLabel}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={creating}
            fullWidth
            placeholder={translations.households.householdNamePlaceholder}
            sx={{ marginBottom: 3 }}
            inputProps={{ maxLength: 255 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
            <Button onClick={handleClose} disabled={creating}>
              {translations.common.cancel}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={creating || !name.trim()}
              startIcon={creating ? <CircularProgress size={20} /> : null}
            >
              {creating
                ? translations.households.creating
                : translations.households.createHousehold}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CreateHouseholdDrawer;
